<template>
  <div class="settings-container tw-col-span-3">
    <div class="communication-preferences">
      <router-link to="/donor-settings" class="communication-preferences__back">
        ← {{ $t("dashboard.donor_communication_prefs_back_link", "Back to settings") }}
      </router-link>

      <h4>{{ $t("dashboard.donor_communication_prefs_title", "Communication preferences") }}</h4>
      <p class="communication-preferences__subtitle">
        {{
          $t(
            "dashboard.donor_communication_prefs_subtitle",
            "Choose which campaigns can contact you by email and SMS"
          )
        }}
      </p>

      <div v-if="pageLoading" class="communication-preferences__loading">
        <LdsSpinner />
      </div>

      <template v-else-if="subsError">
        <p class="communication-preferences__error">
          {{
            $t("dashboard.donor_communication_prefs_load_error", "Couldn't load your preferences.")
          }}
        </p>
        <button class="bttn bttn--sm bttn--orange" @click="load">
          {{ $t("dashboard.donor_communication_prefs_retry_btn", "Retry") }}
        </button>
      </template>

      <template v-else-if="channelViews.length === 0">
        <p class="communication-preferences__empty">
          {{
            $t(
              "dashboard.donor_communication_prefs_no_contacts",
              "We don't have any contact details on file for this account."
            )
          }}
        </p>
      </template>

      <template v-else>
        <div v-if="campaignsError" class="communication-preferences__warning">
          <span>
            {{
              $t(
                "dashboard.donor_communication_prefs_campaigns_load_error",
                "Couldn't load your campaign list."
              )
            }}
          </span>
          <button
            class="bttn bttn--sm bttn--orange"
            :disabled="campaignsLoading"
            @click="loadCampaigns"
          >
            {{ $t("dashboard.donor_communication_prefs_retry_btn", "Retry") }}
          </button>
        </div>

        <channel-preferences
          v-for="channelView in channelViews"
          :key="channelView.channel"
          :channel="channelView.channel"
          :globally-unsubscribed="channelView.globallyUnsubscribed"
          :campaigns="channelView.campaigns"
          :busy-ids="busyIds"
          :busy="narrowBusyChannel === channelView.channel"
          @toggle="onToggle(channelView.channel, $event)"
          @narrow="onNarrow(channelView.channel, $event)"
        />
      </template>
    </div>
  </div>
</template>

<script>
import LdsSpinner from "@/common-components/ui-elements/lds-spinner.vue";
import { notificationsMixin } from "@/mixins";
import channelPreferences from "./subcomponents/communication-preferences/channel-preferences.vue";
import {
  useDonorSubscriptions,
  useDonorDonatedCampaigns,
} from "./compositions/communication-preferences";

export default {
  components: {
    LdsSpinner,
    channelPreferences,
  },
  mixins: [notificationsMixin],
  setup() {
    const subscriptions = useDonorSubscriptions();
    const donated = useDonorDonatedCampaigns();

    return {
      channels: subscriptions.channels,
      fetchSubscriptions: subscriptions.fetchSubscriptions,
      unsubscribeCampaign: subscriptions.unsubscribeCampaign,
      resubscribeCampaign: subscriptions.resubscribeCampaign,
      narrowChannel: subscriptions.narrow,
      donatedCampaigns: donated.campaigns,
      campaignsLoading: donated.loading,
      fetchCampaigns: donated.fetchCampaigns,
    };
  },
  data() {
    return {
      pageLoading: true,
      subsError: false,
      campaignsError: false,
      busyIds: [],
      narrowBusyChannel: null,
    };
  },
  computed: {
    channelViews() {
      return this.channels.map((channel) => {
        const unsubscribedIds = (channel.unsubscribed_campaign_ids || []).map(Number);
        const campaigns = this.donatedCampaigns.map((campaign) => ({
          ...campaign,
          unsubscribed: unsubscribedIds.includes(Number(campaign.id)),
        }));

        // Opt-outs must never be invisible: render campaigns the donor opted
        // out of even when they're missing from the donation-derived list.
        const knownIds = new Set(this.donatedCampaigns.map((campaign) => Number(campaign.id)));
        for (const id of unsubscribedIds) {
          if (!knownIds.has(id)) {
            campaigns.push({
              id,
              name: `${this.$t(
                "dashboard.donor_communication_prefs_unknown_campaign",
                "Campaign"
              )} #${id}`,
              orgName: "",
              unsubscribed: true,
            });
          }
        }

        return {
          channel: channel.channel,
          globallyUnsubscribed: Boolean(channel.globally_unsubscribed),
          campaigns,
        };
      });
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.pageLoading = true;
      this.subsError = false;

      await Promise.all([
        this.fetchSubscriptions().catch(() => {
          this.subsError = true;
        }),
        this.loadCampaigns(),
      ]);

      this.pageLoading = false;
    },

    async loadCampaigns() {
      this.campaignsError = false;

      await this.fetchCampaigns().catch(() => {
        this.campaignsError = true;
      });
    },

    async onToggle(channel, { campaignId, subscribed }) {
      const id = Number(campaignId);

      if (this.busyIds.includes(id)) {
        return;
      }
      this.busyIds.push(id);

      try {
        if (subscribed) {
          await this.resubscribeCampaign(channel, id);
        } else {
          await this.unsubscribeCampaign(channel, id);
        }
        this.patchChannel(channel, id, subscribed);
      } catch (e) {
        this.$_notificationsMixin_handleCatch(e);
      } finally {
        this.busyIds = this.busyIds.filter((busyId) => busyId !== id);
      }
    },

    patchChannel(channel, campaignId, subscribed) {
      const target = this.channels.find((c) => c.channel === channel);

      if (!target) {
        return;
      }

      const ids = (target.unsubscribed_campaign_ids || [])
        .map(Number)
        .filter((id) => id !== campaignId);

      if (!subscribed) {
        ids.push(campaignId);
      }

      this.$set(target, "unsubscribed_campaign_ids", ids);
    },

    async onNarrow(channel, unwantedCampaignIds) {
      // Confirmation happens inline in narrow-preferences-panel rather than
      // via $bvModal.msgBoxConfirm — keeps the re-consent step in context and
      // avoids modal transitions (which stall in background/occluded tabs).
      this.narrowBusyChannel = channel;

      try {
        await this.narrowChannel(channel, unwantedCampaignIds);
        // Narrow changes both the global flag and the campaign list — refetch
        // rather than hand-patching, and keep the legacy settings screen's
        // mailing_unsubscribed/sms_unsubscribed flags in sync.
        await this.fetchSubscriptions();
        this.$store.dispatch("getDonorAccountInfo");
        this.$_notificationsMixin_makeToast(
          "Success!",
          this.$t("dashboard.donor_communication_prefs_saved", "Preferences updated"),
          "success"
        );
      } catch (e) {
        this.$_notificationsMixin_handleCatch(e);
      } finally {
        this.narrowBusyChannel = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/_vars.scss";
.settings-container {
  padding: 45px 35px;
  box-shadow: 0 8px 32px 0 rgba(35, 40, 71, 0.08);
  background-color: #ffffff;
}
.communication-preferences {
  padding: 0 15px;
  font-size: 20px;
  color: #414359;
  h4 {
    font: 26px/1.2 "VitoExtended-Bold";
    margin-bottom: 15px;
  }
  &__back {
    display: inline-block;
    margin-bottom: 20px;
    font-size: 16px;
  }
  &__subtitle {
    font-size: 17px;
    color: #7c8a97;
  }
  &__loading {
    display: flex;
    justify-content: center;
    padding: 40px 0;
  }
  &__error,
  &__empty {
    font-size: 17px;
  }
  &__warning {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    margin-top: 20px;
    padding: 12px 20px;
    border-radius: 4px;
    background-color: #fff8e6;
    font-size: 16px;
  }
}
</style>
